import { useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Box, Card, Typography, CircularProgress, useTheme } from '@mui/material'

import { getPublicReport } from '../../services/api/AnalyticsDataService'
import { BarRow, pct, num, countryFlag, prettyLabel } from '../studio/tabs/analytics/analyticsUi'

function Stat({ value, label }) {
    return (
        <Box sx={{ textAlign: 'center', minWidth: 110 }}>
            <Typography sx={{ fontWeight: 800, fontSize: '1.8rem', color: '#111' }}>{value}</Typography>
            <Typography sx={{ fontSize: '.82rem', color: '#888' }}>{label}</Typography>
        </Box>
    )
}

export default function PublicReportPage() {
    const theme = useTheme()
    const { token } = useParams()
    const { data, isLoading, isError } = useQuery({
        queryKey: ['public-report', token],
        queryFn: () => getPublicReport(token),
        retry: false,
    })

    if (isLoading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}><CircularProgress /></Box>
    }
    if (isError || !data) {
        return (
            <Box sx={{ textAlign: 'center', mt: 10 }}>
                <Typography variant="h6">This report is no longer available.</Typography>
            </Box>
        )
    }

    const m = data.metrics || {}
    const b = data.breakdown || {}

    return (
        <Box sx={{ maxWidth: 760, mx: 'auto', p: { xs: 2, md: 4 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography sx={{ fontWeight: 900, color: theme.palette.primary.main }}>Vibio</Typography>
            </Box>
            <Typography variant="h4" sx={{ fontWeight: 800, mb: 0.5 }}>{data.campaignName}</Typography>
            <Typography sx={{ color: '#888', mb: 3 }}>{data.rangeStart} &#8594; {data.rangeEnd}</Typography>

            <Card variant="outlined" sx={{ borderRadius: 3, p: 3, mb: 2 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, justifyContent: 'space-around' }}>
                    <Stat value={num(m.visitors)} label="visitors" />
                    <Stat value={num(m.pageViews)} label="page views" />
                    <Stat value={pct(m.takeActionRate)} label="took action" />
                    <Stat value={num(m.focusLinkClicks)} label="sponsor clicks" />
                    {m.avgWatchSeconds != null && <Stat value={`${m.avgWatchSeconds}s`} label="avg watch" />}
                </Box>
            </Card>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                <Card variant="outlined" sx={{ borderRadius: 3, p: 2.6 }}>
                    <Typography sx={{ fontSize: '.72rem', letterSpacing: 1, fontWeight: 700, color: '#999', mb: 1.5 }}>TOP LOCATIONS</Typography>
                    {(b.topCountries || []).length
                        ? b.topCountries.map((c, i) => <BarRow key={i} label={c.label} share={c.share} leading={countryFlag(c.label)} />)
                        : <Typography sx={{ color: '#aaa', fontSize: '.9rem' }}>-</Typography>}
                </Card>
                <Card variant="outlined" sx={{ borderRadius: 3, p: 2.6 }}>
                    <Typography sx={{ fontSize: '.72rem', letterSpacing: 1, fontWeight: 700, color: '#999', mb: 1.5 }}>DEVICES</Typography>
                    {(b.deviceSplit || []).length
                        ? b.deviceSplit.map((d, i) => <BarRow key={i} label={prettyLabel(d.label)} share={d.share} />)
                        : <Typography sx={{ color: '#aaa', fontSize: '.9rem' }}>-</Typography>}
                </Card>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mt: 4, color: '#aaa' }}>
                <Typography sx={{ fontSize: '.8rem' }}>Data by Vibio</Typography>
            </Box>
        </Box>
    )
}
